import React, { useEffect, useRef, useState, memo, useCallback } from "react";
import { store, view } from "@risingstack/react-easy-state";
import KinesisVideo from "aws-sdk/clients/kinesisvideo";
import KinesisVideoSignalingChannels from "aws-sdk/clients/kinesisvideosignalingchannels";
import { SignalingClient } from "amazon-kinesis-video-streams-webrtc";
import Widget from "./widget.js";
import AWS from "aws-sdk";
import config from "../config";
import "./common.css";
import CountdownTimer from "./CountdownTimer";

import "./VideoLayout.css";
import ChatWidget from "./ChatWidget";
import ChatIcon from "./ChatIcon";
import StudentAlertButton from "./StudentAlertButton";
import StudentAlertPopup from "./StudentAlertPopup";
import VerificationStatus from "./VerificationStatus";
import UploadStatus from "./UploadStatus";
import VerificationInstructionsPopup from "./VerificationInstructionsPopup";

// Update AWS configuration using config instead of process.env
AWS.config.update({
  region: config.aws.region,
  credentials: {
    accessKeyId: config.aws.credentials.accessKeyId,
    secretAccessKey: config.aws.credentials.secretAccessKey,
  },
});

// Used to determine / validate options in form components:
const OPTIONS = {
  TRAVERSAL: {
    STUN_TURN: "stunTurn",
    TURN_ONLY: "turnOnly",
    DISABLED: "disabled",
  },
  ROLE: {
    MASTER: "MASTER",
  },
  RESOLUTION: {
    WIDESCREEN: "widescreen",
    FULLSCREEN: "fullscreen",
  },
};

// Stores state across components (react-easy-state is super easy to use!)
export const state = store({
  // These are config params set by the user:
  role: window.appState?.role || OPTIONS.ROLE.MASTER,
  channelName: window.appState?.channelName,
  examStartTime: window.appState?.examStartTime,
  examEndTime: window.appState?.examEndTime,
  clientId: getRandomClientId(),
  endpoint: null,
  // These will be true by default and not changeable
  sendVideo: true,
  sendAudio: true,
  openDataChannel: true,
  useTrickleICE: true,
  resolution: OPTIONS.RESOLUTION.WIDESCREEN,
  natTraversal: OPTIONS.TRAVERSAL.STUN_TURN,
  messageToSend: "",
  playerIsStarted: false,

  // Initialize behavior alerts as an empty array
  behaviorAlerts: [],

  // These are set when user starts video; a few of them are only used when you start the stream as MASTER:
  signalingClient: null,
  localStream: null,
  localView: null,
  remoteView: null,
  dataChannel: null,
  peerConnectionStatsInterval: null,
  peerConnectionByClientId: {},
  dataChannelByClientId: [],
  receivedMessages: "",
  remoteViews: {},
  videoClient: {},
  channelARN: null,
  isMasterConnected: false,
  isRecording: false, // To track recording status
  recordingInterval: null, // To store the interval reference
  accessKey: config.aws.credentials.accessKeyId,
  secretAccessKey: config.aws.credentials.secretAccessKey,
  sessionToken: "",
  region: config.aws.region,
  activeChat: "broadcast",
  unreadMessages: {},
  messages: {
    broadcast: [],
  },
  mode: "exam", // default mode
  verifiedStudents: {},
  faceVerificationStatus: {}, // Store face verification status for each student
  uploadPhaseEndTime: null,
  setUploadPhaseEndTime: (time) => {
    state.uploadPhaseEndTime = time;
  },
});

// Add at the top with other state definitions
export const alertState = store({
  unreadAlerts: {}, // Track unread counts per student
  readAlertIds: {}, // Track which alerts have been read per student
  openPopups: {}, // Track which student popups are open
});

// Add at the top with other state definitions
const chatState = store({
  activeChat: "broadcast",
  messages: { broadcast: [] },
  unreadMessages: {},
  uploadingStudents: {}, // Add this to track uploading students
});

// Standalone verification status component
const VerificationBadge = memo(({ clientId, verifiedStudents }) => {
  const status = verifiedStudents[clientId];
  if (status === undefined) return null;

  return (
    <div className={`verification-badge ${status ? "verified" : "rejected"}`}>
      {status ? "Verified" : "Rejected"}
    </div>
  );
});

// Verification controls component
const VerificationControls = memo(
  ({ studentId, verifiedStudents, setVerifiedStudents }) => {
    const isVerified = verifiedStudents[studentId];

    const handleVerification = useCallback(
      async (isAccepted) => {
        const message = {
          type: "VERIFICATION_STATUS",
          status: isAccepted ? "ACCEPTED" : "REJECTED",
          studentId: studentId,
        };

        // Only send to the specific student's data channel
        if (
          state.dataChannelByClientId[studentId] &&
          state.dataChannelByClientId[studentId].readyState === "open"
        ) {
          console.log("[MASTER] Sending verification message:", message);
          state.dataChannelByClientId[studentId].send(JSON.stringify(message));
        }

        // If student is accepted, make API call to update verification status
        if (isAccepted) {
          try {
            const examId = window.appState?.examId;
            if (!examId) {
              console.error("[MASTER] No examId found in appState");
              return;
            }

            const response = await fetch(
              `https://procbackend.elcarreira.com/proctor/setStudentverification?studentId=${studentId}&examId=${examId}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            if (!response.ok) {
              console.error(
                "[MASTER] Failed to update student verification status:",
                await response.text()
              );
            }
          } catch (error) {
            console.error(
              "[MASTER] Error updating student verification status:",
              error
            );
          }
        }

        setVerifiedStudents((prev) => ({
          ...prev,
          [studentId]: isAccepted,
        }));
      },
      [studentId, setVerifiedStudents]
    );

    // If already verified/rejected, don't render anything
    if (isVerified !== undefined) {
      return null;
    }

    return (
      <div className="verification-controls">
        <div className="verification-buttons">
          <button
            className="accept-button"
            onClick={() => handleVerification(true)}
          >
            Accept
          </button>
          <button
            className="reject-button"
            onClick={() => handleVerification(false)}
          >
            Reject
          </button>
        </div>
      </div>
    );
  }
);

// Remote video player component
const RemoteVideoPlayer = view(
  ({
    clientId,
    mode,
    verifiedStudents,
    setVerifiedStudents,
    handleChatClick,
    remoteViews,
    setRemoteVideoRef,
    state,
  }) => {
    const [showAlertPopup, setShowAlertPopup] = useState(false);
    const videoRef = useCallback(
      (el) => setRemoteVideoRef(el, clientId),
      [clientId, setRemoteVideoRef]
    );

    const handleAlertClick = () => {
      const willShow = !showAlertPopup;
      setShowAlertPopup(willShow);
      if (willShow) {
        const studentAlerts = state.behaviorAlerts.filter(
          (alert) => alert.viewerId === clientId
        );
        alertState.readAlertIds[clientId] = studentAlerts.map(
          (alert) => alert.id
        );
        alertState.unreadAlerts[clientId] = 0;
        alertState.openPopups[clientId] = true;
      } else {
        alertState.openPopups[clientId] = false;
      }
    };

    const handleChatIconClick = useCallback(() => {
      handleChatClick(clientId);
      if (chatState.unreadMessages[clientId]) {
        chatState.unreadMessages[clientId] = 0;
        console.log("[MASTER] Reset unread count for client:", clientId);
      }
    }, [clientId, handleChatClick]);

    // Get verification status for this student
    const verificationStatus = state.faceVerificationStatus[clientId] || {
      status: "pending",
      confidence: 0,
    };

    return (
      <div className="video-container">
        <video ref={videoRef} autoPlay playsInline />
        {mode === "exam" && (
          <>
            <div className="video-label">Viewer {clientId.slice(-4)}</div>
            <ChatIcon
              unreadCount={chatState.unreadMessages[clientId] || 0}
              onClick={handleChatIconClick}
              key={`chat-icon-${clientId}-${
                chatState.unreadMessages[clientId] || 0
              }`}
            />
            <StudentAlertButton
              studentId={clientId}
              alerts={state.behaviorAlerts || []}
              onClick={handleAlertClick}
              isPopupOpen={showAlertPopup}
            />
            {showAlertPopup && (
              <StudentAlertPopup
                studentId={clientId}
                onClose={() => setShowAlertPopup(false)}
              />
            )}
            {chatState.uploadingStudents[clientId] && <UploadStatus />}
          </>
        )}
        {mode === "verification" && (
          <>
            <div className="video-label">Viewer {clientId.slice(-4)}</div>
            <VerificationStatus
              status={
                verificationStatus.status === "MATCH"
                  ? "MATCH"
                  : verificationStatus.status === "NO_MATCH"
                  ? "NO_MATCH"
                  : "pending"
              }
              confidence={verificationStatus.confidence}
            />
            <VerificationControls
              studentId={clientId}
              verifiedStudents={verifiedStudents}
              setVerifiedStudents={setVerifiedStudents}
            />
          </>
        )}
      </div>
    );
  }
);

// Add this new component before VideoPlayers
const VerificationTimer = memo(({ mode, onVerificationComplete }) => {
  const [timeToStart, setTimeToStart] = useState(null);
  const [canStartExam, setCanStartExam] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (mode === "verification" && window.appState?.examStartTime) {
      // Show verification instructions popup when verification mode starts
      setShowInstructions(true);

      const examStartTime = new Date(window.appState.examStartTime);

      const updateTimer = () => {
        const now = new Date();
        const diffInSeconds = Math.floor((examStartTime - now) / 1000);

        if (diffInSeconds <= 0) {
          setCanStartExam(true);
          setTimeToStart(0);
          return;
        }

        setTimeToStart(diffInSeconds);
      };

      // Initial update
      updateTimer();

      // Update every second if time hasn't passed
      const timer = setInterval(() => {
        updateTimer();
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [mode]);

  const handleProceedToExam = async () => {
    // Make API call to set exam start flag
    try {
      const examId = window.appState?.examId;
      if (!examId) {
        console.error("[MASTER] No examId found in appState");
        return;
      }

      const response = await fetch(
        `https://procbackend.elcarreira.com/proctor/setExamStartFlag?examId=${examId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        console.error(
          "[MASTER] Failed to set exam start flag:",
          await response.text()
        );
        return;
      }
    } catch (error) {
      console.error("[MASTER] Error setting exam start flag:", error);
      return;
    }

    // Clear all alert and chat related states
    state.behaviorAlerts = [];
    state.messages = {
      broadcast: [],
    };
    chatState.unreadMessages = {};
    alertState.unreadAlerts = {};
    alertState.readAlertIds = {};
    alertState.openPopups = {};

    // Send mode change message to all verified students
    Object.keys(state.dataChannelByClientId).forEach((clientId) => {
      if (state.dataChannelByClientId[clientId]?.readyState === "open") {
        const message = {
          type: "MODE_CHANGE",
          mode: "exam",
        };
        console.log(
          "[MASTER] Sending mode change message to",
          clientId,
          ":",
          message
        );
        state.dataChannelByClientId[clientId].send(JSON.stringify(message));
      }
    });

    // Call the original onVerificationComplete
    onVerificationComplete();
  };

  const handleCloseInstructions = () => {
    setShowInstructions(false);
  };

  if (mode !== "verification") return null;

  // Format time display
  const formatTime = (seconds) => {
    if (seconds === null) return "";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <>
      <div className="verification-timer">
        {timeToStart > 0
          ? `Time until exam: ${formatTime(timeToStart)}`
          : "Exam can start now"}
      </div>
      {canStartExam && (
        <button className="proceed-button" onClick={handleProceedToExam}>
          Start Exam
        </button>
      )}
      {showInstructions && (
        <VerificationInstructionsPopup onClose={handleCloseInstructions} />
      )}
    </>
  );
});

//------------------------------------------------------------------------------
const KinesisWebRTC = view(
  ({
    onStreamStopped,
    mode = "exam",
    verifiedStudents,
    setVerifiedStudents,
    onVerificationComplete,
  }) => {
    // Only proceed with initialization if exam hasn't ended
    const localViewRef = useRef(null);
    state.localView = localViewRef;
    state.remoteView = useRef(null);
    state.mode = mode;

    useEffect(() => {
      // Check if media devices are supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error("[MASTER] Media devices not supported in this browser");
        alert(
          "Your browser does not support media devices. Please use a modern browser."
        );
        return;
      }

      if (window.appState) {
        console.log("[MASTER] Initializing with appState:", window.appState);
        state.role = window.appState.role;
        state.channelName = window.appState.channelName;
      }
      state.clientId = getRandomClientId();
      if (window.appState?.autoStart && !state.playerIsStarted) {
        startPlayer();
      }

      // Cleanup function
      return () => {
        if (state.playerIsStarted) {
          stopPlayer();
        }
      };
    }, []);

    useEffect(() => {
      // Check if exam has ended
      if (window.appState?.examEndTime) {
        const checkExamEnd = setInterval(() => {
          const now = new Date();
          const endTime = new Date(window.appState.examEndTime);
          if (now >= endTime) {
            console.log("Exam time has ended, ending exam in 5 seconds...");

            // Call API to update exam end status
            const examId = window.appState?.examId;
            if (examId) {
              console.log(
                "[MASTER] Updating exam end status for exam:",
                examId
              );
              fetch(
                `https://procbackend.elcarreira.com/exam/updateExamEndStatus?examId=${examId}`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              )
                .then((response) => {
                  if (!response.ok) {
                    return response.text().then((text) => {
                      throw new Error(
                        `Failed to update exam end status: ${text}`
                      );
                    });
                  }
                  return response.json();
                })
                .then((data) => {
                  console.log(
                    "[MASTER] Exam end status updated successfully:",
                    data
                  );
                })
                .catch((error) => {
                  console.error(
                    "[MASTER] Error updating exam end status:",
                    error
                  );
                });
            }

            clearInterval(checkExamEnd);

            // Set a timeout to stop the session after 5 seconds
            setTimeout(() => {
              console.log(
                "5 seconds passed after exam end, stopping session now"
              );
              stopPlayer();
              if (onStreamStopped) {
                onStreamStopped();
              }
            }, 5000);
          }
        }, 1000);

        return () => {
          if (checkExamEnd) {
            clearInterval(checkExamEnd);
          }
        };
      }
    }, [onStreamStopped]);

    return (
      <div className="min-h-screen bg-gray-100">
        <Widget>
          {state.playerIsStarted ? (
            <VideoPlayers
              onStreamStopped={onStreamStopped}
              mode={mode}
              verifiedStudents={verifiedStudents}
              setVerifiedStudents={setVerifiedStudents}
              onVerificationComplete={onVerificationComplete}
              localViewRef={localViewRef}
            />
          ) : (
            ""
          )}
        </Widget>
      </div>
    );
  }
);

//------------------------------------------------------------------------------
/**
 *  Configuration Parameters:
 *
 *  1. Access Key ( NOT TO CHANGE )
 *  2. Secret Key ( NOT TO CHANGE )
 *  3. Session Token (Optional)
 *  4. region ( NOT TO CHANGE )
 *  5. Channel Name [ Changes everytime ]
 *  6. ClientId ( not to change | we need to use studentId instead )
 *  7. NAT Traversal  STUN / TURN ( NOT TO CHANGE )
 *  8. Trickle ICE (NOT TO CHANGE)
 *  9. Resolution ( changable)
 *  10. Role ( Master | Viewer )
 */

// create a separate component for the message controls
const MessageControls = view(() => {
  const { activeChat } = chatState;

  const handleSendMessage = (message) => {
    if (message === "__switch_to_broadcast__") {
      chatState.activeChat = "broadcast";
      return;
    }

    const timestamp = new Date().toISOString();
    const messageObj = {
      type: "CHAT",
      message: message,
      sender: state.clientId,
      recipient: activeChat,
    };

    if (state.role === OPTIONS.ROLE.MASTER) {
      if (!chatState.messages[activeChat]) {
        chatState.messages[activeChat] = [];
      }
      chatState.messages[activeChat].push({
        timestamp,
        message,
        sender: "You",
        type: activeChat === "broadcast" ? "broadcast" : "private",
      });

      if (activeChat === "broadcast") {
        Object.keys(state.dataChannelByClientId).forEach((clientId) => {
          try {
            if (state.dataChannelByClientId[clientId]?.readyState === "open") {
              state.dataChannelByClientId[clientId].send(
                JSON.stringify(messageObj)
              );
              // Remove notification increment for broadcast messages since they're from the sender
            }
          } catch (e) {
            console.error("[MASTER] Send DataChannel error:", e.toString());
          }
        });
      } else {
        try {
          if (state.dataChannelByClientId[activeChat]?.readyState === "open") {
            state.dataChannelByClientId[activeChat].send(
              JSON.stringify(messageObj)
            );
          }
        } catch (e) {
          console.error("[MASTER] Send DataChannel error:", e.toString());
        }
      }
    }
    // ... rest of the function remains same
  };

  return (
    <ChatWidget
      messages={chatState.messages[activeChat] || []}
      onSendMessage={handleSendMessage}
      recipient={
        activeChat === "broadcast" ? "Everyone" : `Viewer ${activeChat}`
      }
    />
  );
});

// Main VideoPlayers component
const VideoPlayers = view(
  ({
    onStreamStopped,
    mode,
    verifiedStudents,
    setVerifiedStudents,
    onVerificationComplete,
    localViewRef,
  }) => {
    const { role, remoteView, remoteViews } = state;
    const hasViewers = Object.keys(remoteViews || {}).length > 0;
    const [examEnded, setExamEnded] = useState(false);

    const handleStopStream = () => {
      // Call API to update exam end status when manually stopping
      const examId = window.appState?.examId;
      if (examId) {
        console.log("[MASTER] Updating exam end status for exam:", examId);
        fetch(
          `https://procbackend.elcarreira.com/exam/updateExamEndStatus?examId=${examId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
          .then((response) => {
            if (!response.ok) {
              return response.text().then((text) => {
                throw new Error(`Failed to update exam end status: ${text}`);
              });
            }
            return response.json();
          })
          .then((data) => {
            console.log("[MASTER] Exam end status updated successfully:", data);
          })
          .catch((error) => {
            console.error("[MASTER] Error updating exam end status:", error);
          });
      }

      stopPlayer();
      if (onStreamStopped) {
        onStreamStopped();
      }
    };

    const setRemoteVideoRef = (el, clientId) => {
      if (el) {
        el.srcObject = remoteViews[clientId].srcObject;
        state.remoteViews[clientId] = el;
      }
    };

    const handleChatClick = useCallback((targetClientId) => {
      console.log(`Switching chat to ${targetClientId}`);
      chatState.activeChat = targetClientId;
      if (!chatState.messages[targetClientId]) {
        chatState.messages[targetClientId] = [];
      }
      chatState.unreadMessages[targetClientId] = 0;
      document.querySelector(".chat-toggle-button")?.click();
    }, []);

    useEffect(() => {
      // Check if exam has ended
      if (window.appState?.examEndTime) {
        const checkExamEnd = setInterval(() => {
          const now = new Date();
          const endTime = new Date(window.appState.examEndTime);
          if (now >= endTime) {
            console.log("Exam time has ended, ending exam in 5 seconds...");

            // Call API to update exam end status
            const examId = window.appState?.examId;
            if (examId) {
              console.log(
                "[MASTER] Updating exam end status for exam:",
                examId
              );
              fetch(
                `https://procbackend.elcarreira.com/exam/updateExamEndStatus?examId=${examId}`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                }
              )
                .then((response) => {
                  if (!response.ok) {
                    return response.text().then((text) => {
                      throw new Error(
                        `Failed to update exam end status: ${text}`
                      );
                    });
                  }
                  return response.json();
                })
                .then((data) => {
                  console.log(
                    "[MASTER] Exam end status updated successfully:",
                    data
                  );
                })
                .catch((error) => {
                  console.error(
                    "[MASTER] Error updating exam end status:",
                    error
                  );
                });
            }

            clearInterval(checkExamEnd);

            // Set a timeout to stop the session after 5 seconds
            setTimeout(() => {
              console.log(
                "5 seconds passed after exam end, stopping session now"
              );
              stopPlayer();
              if (onStreamStopped) {
                onStreamStopped();
              }
            }, 5000);
          }
        }, 1000);

        return () => {
          if (checkExamEnd) {
            clearInterval(checkExamEnd);
          }
        };
      }
    }, [onStreamStopped]);

    return (
      <div>
        <div className="stream-header">
          <div className="stream-title">
            {mode === "verification" ? "Student Verification" : "Live Stream"}
          </div>
          <div className="stream-controls">
            {mode === "exam" && !examEnded && window.appState?.examEndTime && (
              <CountdownTimer endTime={window.appState.examEndTime} />
            )}
            {mode === "exam" && examEnded && (
              <div className="ending-message">
                Exam time has ended. Session will close in 5 seconds...
              </div>
            )}
            <VerificationTimer
              mode={mode}
              onVerificationComplete={onVerificationComplete}
            />
            <button onClick={handleStopStream} className="stop-button">
              Stop Exam
            </button>
          </div>
        </div>

        <div className="video-alerts-container">
          <div
            className={`video-grid ${
              role === OPTIONS.ROLE.MASTER && !hasViewers
                ? "master-alone"
                : "has-viewers"
            }`}
          >
            {/* Local View */}
            <div className="video-container">
              <video ref={localViewRef} autoPlay playsInline muted />
              <div className="video-label">You (Local)</div>
            </div>

            {/* Remote Views */}
            {role === OPTIONS.ROLE.MASTER ? (
              Object.keys(remoteViews || {}).map((clientId) => (
                <RemoteVideoPlayer
                  key={clientId}
                  clientId={clientId}
                  mode={mode}
                  verifiedStudents={verifiedStudents}
                  setVerifiedStudents={setVerifiedStudents}
                  handleChatClick={handleChatClick}
                  remoteViews={remoteViews}
                  setRemoteVideoRef={setRemoteVideoRef}
                  state={state}
                />
              ))
            ) : (
              <div className="video-container">
                <video ref={remoteView} autoPlay playsInline />
                <div className="video-label">Master</div>
              </div>
            )}
          </div>

          {/* Only show chat in exam mode */}
          {mode === "exam" && role === OPTIONS.ROLE.MASTER && (
            <MessageControls />
          )}
        </div>
      </div>
    );
  }
);

//------------------------------------------------------------------------------
async function startPlayer() {
  // First check if exam has already ended
  if (window.appState?.examEndTime) {
    const now = new Date();
    const endTime = new Date(window.appState.examEndTime);
    if (now >= endTime) {
      alert("Exam has already ended. You cannot join now.");
      // Ensure we don't set playerIsStarted to true
      state.playerIsStarted = false;
      // Call onStreamStopped to properly clean up and redirect if needed
      if (window.onStreamStopped) {
        window.onStreamStopped();
      }
      return false;
    }

    // Check if trying to start more than 30 minutes before exam
    const examStartTime = new Date(window.appState.examStartTime);
    const thirtyMinsBefore = new Date(examStartTime.getTime() - 30 * 60 * 1000);
    if (now < thirtyMinsBefore) {
      alert("You can only join within 30 minutes of exam start time.");
      state.playerIsStarted = false;
      return false;
    }
  }

  if (state.role === OPTIONS.ROLE.VIEWER) {
    const masterConnected = await checkMasterStatus();
    if (!masterConnected) {
      alert(
        "Master is not connected. Please wait for master to connect first."
      );
      // Ensure we don't set playerIsStarted to true
      state.playerIsStarted = false;
      return false;
    }
  }

  // Only set playerIsStarted to true if all checks pass
  state.playerIsStarted = true;

  if (state.role === OPTIONS.ROLE.MASTER) {
    try {
      await startPlayerForMaster();
      return true;
    } catch (error) {
      console.error("[MASTER] Error starting master:", error);
      state.playerIsStarted = false;
      alert("Failed to start the exam session. Please try again.");
      return false;
    }
  } else {
    return false;
  }
}

//------------------------------------------------------------------------------
async function startPlayerForMaster() {
  console.log("[MASTER] Starting master with channel:", state.channelName);

  // Create KVS client
  console.log("Creating KVS client...");
  const kinesisVideoClient = new KinesisVideo({
    region: config.aws.region,
    endpoint: state.endpoint || null,
    correctClockSkew: true,
    accessKeyId: config.aws.credentials.accessKeyId,
    secretAccessKey: config.aws.credentials.secretAccessKey,
  });

  state.videoClient = kinesisVideoClient;

  // First try to create the channel
  try {
    console.log(
      "[MASTER] Attempting to create signaling channel:",
      state.channelName
    );
    await kinesisVideoClient
      .createSignalingChannel({
        ChannelName: state.channelName,
      })
      .promise();
    console.log("[MASTER] Signaling channel created successfully");
  } catch (error) {
    if (error.code !== "ResourceInUseException") {
      console.error("[MASTER] Error creating signaling channel:", error);
      // Only throw if it's not a "channel already exists" error
      throw error;
    }
    console.log("[MASTER] Channel already exists, proceeding...");
  }

  // Now get the channel ARN
  try {
    console.log("[MASTER] Getting channel ARN...");
    const describeResponse = await kinesisVideoClient
      .describeSignalingChannel({
        ChannelName: state.channelName,
      })
      .promise();

    const channelARN = describeResponse.ChannelInfo.ChannelARN;
    state.channelARN = channelARN;
    console.log("[MASTER] Channel ARN:", channelARN);

    // Get signaling channel endpoints
    console.log("[MASTER] Getting signaling channel endpoints...");
    const getSignalingChannelEndpointResponse = await kinesisVideoClient
      .getSignalingChannelEndpoint({
        ChannelARN: channelARN,
        SingleMasterChannelEndpointConfiguration: {
          Protocols: ["WSS", "HTTPS"],
          Role: state.role,
        },
      })
      .promise();

    const endpointsByProtocol =
      getSignalingChannelEndpointResponse.ResourceEndpointList.reduce(
        (endpoints, endpoint) => {
          endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
          return endpoints;
        },
        {}
      );
    console.log("[MASTER] Endpoints: ", endpointsByProtocol);

    // Create Signaling Client
    console.log(`Creating signaling client...`);
    state.signalingClient = new SignalingClient({
      channelARN,
      channelEndpoint: endpointsByProtocol.WSS,
      role: state.role,
      region: config.aws.region,
      systemClockOffset: kinesisVideoClient.config.systemClockOffset,
      credentials: {
        accessKeyId: config.aws.credentials.accessKeyId,
        secretAccessKey: config.aws.credentials.secretAccessKey,
      },
    });

    console.log("state.signalingClient ---- ", state.signalingClient);

    // Get ICE server configuration
    console.log("Creating ICE server configuration...");
    const kinesisVideoSignalingChannelsClient =
      new KinesisVideoSignalingChannels({
        region: config.aws.region,
        endpoint: endpointsByProtocol.HTTPS,
        correctClockSkew: true,
        accessKeyId: config.aws.credentials.accessKeyId,
        secretAccessKey: config.aws.credentials.secretAccessKey,
      });

    console.log("Getting ICE server config...");
    const getIceServerConfigResponse = await kinesisVideoSignalingChannelsClient
      .getIceServerConfig({
        ChannelARN: channelARN,
      })
      .promise();

    const iceServers = [];
    if (state.natTraversal === OPTIONS.TRAVERSAL.STUN_TURN) {
      console.log("Getting STUN servers...");
      iceServers.push({
        urls: `stun:stun.kinesisvideo.${config.aws.region}.amazonaws.com:443`,
      });
    }

    if (state.natTraversal !== OPTIONS.TRAVERSAL.DISABLED) {
      console.log("Getting TURN servers...");
      getIceServerConfigResponse.IceServerList.forEach((iceServer) =>
        iceServers.push({
          urls: iceServer.Uris,
          username: iceServer.Username,
          credential: iceServer.Password,
        })
      );
    }

    const configuration = {
      iceServers,
      iceTransportPolicy:
        state.natTraversal === OPTIONS.TRAVERSAL.TURN_ONLY ? "relay" : "all",
    };

    // Define constraints before trying to get user media
    const constraints = {
      video: state.sendVideo
        ? {
            width: {
              ideal:
                state.resolution === OPTIONS.RESOLUTION.WIDESCREEN ? 1280 : 640,
            },
            height: {
              ideal:
                state.resolution === OPTIONS.RESOLUTION.WIDESCREEN ? 720 : 480,
            },
          }
        : false,
      audio: state.sendAudio,
    };

    // Get a stream from the webcam and display it in the local view.
    // If no video/audio needed, no need to request for the sources.
    if (state.sendVideo || state.sendAudio) {
      try {
        console.log("[MASTER] Requesting media with constraints:", constraints);
        state.localStream = await navigator.mediaDevices.getUserMedia(
          constraints
        );

        // Ensure localView is available before setting srcObject
        if (!state.localView) {
          console.error("[MASTER] localView is not initialized");
          throw new Error("Local view is not initialized");
        }

        if (!state.localView.current) {
          console.error("[MASTER] localView.current is not available");
          throw new Error("Local view reference is not available");
        }

        state.localView.current.srcObject = state.localStream;
        console.log("[MASTER] Successfully set up local stream");
      } catch (e) {
        console.error("[MASTER] Error accessing media devices:", e);
        if (e.name === "NotAllowedError") {
          alert(
            "Please allow access to your camera and microphone to continue."
          );
        } else if (e.name === "NotFoundError") {
          alert(
            "No camera or microphone found. Please check your device connections."
          );
        } else if (e.name === "NotReadableError") {
          alert(
            "Your camera or microphone may be in use by another application."
          );
        } else {
          alert("Error accessing media devices: " + e.message);
        }
        throw e;
      }
    }

    console.log("Adding signalingClient.on open handler...");
    state.signalingClient.on("open", async () => {
      console.log("[MASTER] Connected to signaling service");
      state.isMasterConnected = true;
    });

    console.log("Adding signalingClient.on sdpOffer handler...");

    state.signalingClient.on("sdpOffer", async (offer, remoteClientId) => {
      console.log("[MASTER] Received SDP offer from client: " + remoteClientId);

      // Create a new peer connection using the offer from the given client
      const peerConnection = new RTCPeerConnection(configuration);
      state.peerConnectionByClientId[remoteClientId] = peerConnection;

      // Add connection state monitoring
      peerConnection.addEventListener("connectionstatechange", () => {
        console.log(
          `[MASTER] Connection state changed for client ${remoteClientId}: `,
          peerConnection.connectionState
        );
        console.log(
          `[MASTER] Current peer connections:`,
          Object.keys(state.peerConnectionByClientId)
        );
        console.log(
          `[MASTER] Current remote views:`,
          Object.keys(state.remoteViews)
        );
        console.log(
          `[MASTER] Current data channels:`,
          Object.keys(state.dataChannelByClientId)
        );

        // Only cleanup on definitive disconnection states
        if (
          peerConnection.connectionState === "failed" ||
          peerConnection.connectionState === "closed"
        ) {
          console.log(
            `[MASTER] Connection ${peerConnection.connectionState} for client: ${remoteClientId}`
          );

          // Always clean up video for actual disconnections
          if (state.remoteViews[remoteClientId]) {
            console.log(
              `[MASTER] Cleaning up remote view for client: ${remoteClientId}`
            );
            state.remoteViews[remoteClientId].srcObject = null;
            const newRemoteViews = { ...state.remoteViews };
            delete newRemoteViews[remoteClientId];
            state.remoteViews = newRemoteViews;
          }

          // Clean up peer connection as it needs to be re-established anyway
          if (state.peerConnectionByClientId[remoteClientId]) {
            console.log(
              `[MASTER] Cleaning up peer connection for client: ${remoteClientId}`
            );
            state.peerConnectionByClientId[remoteClientId].close();
            delete state.peerConnectionByClientId[remoteClientId];
          }

          // For unverified students, clean up everything
          if (!state.verifiedStudents[remoteClientId]) {
            console.log(
              `[MASTER] Performing full cleanup for unverified client: ${remoteClientId}`
            );

            // Clean up data channel
            if (state.dataChannelByClientId[remoteClientId]) {
              console.log(
                `[MASTER] Cleaning up data channel for client: ${remoteClientId}`
              );
              delete state.dataChannelByClientId[remoteClientId];
            }

            // Reset chat state to broadcast if this was the active chat
            if (chatState.activeChat === remoteClientId) {
              console.log(`[MASTER] Resetting active chat to broadcast`);
              chatState.activeChat = "broadcast";
            }
          } else {
            console.log(
              `[MASTER] Preserving data channel and chat history for verified client: ${remoteClientId}`
            );
          }
        } else if (peerConnection.connectionState === "disconnected") {
          // For temporary disconnections, just log but don't cleanup
          console.log(
            `[MASTER] Client ${remoteClientId} temporarily disconnected, waiting for reconnection...`
          );
        }
      });

      if (state.openDataChannel) {
        console.log(`Creating data channel for ${remoteClientId}`);
        const dataChannel = peerConnection.createDataChannel("kvsDataChannel");
        state.dataChannelByClientId[remoteClientId] = dataChannel;

        // Handle messages received through the data channel
        dataChannel.onmessage = (event) => {
          try {
            console.log(
              "[MASTER] Raw data channel message received:",
              event.data
            );
            const message = JSON.parse(event.data);
            console.log("[MASTER] Parsed message:", {
              type: message.type,
              viewerId: message.viewerId || message.studentId,
              timestamp: message.timestamp,
              fullMessage: message,
            });

            const timestamp = new Date().toISOString();

            switch (message.type) {
              case "FACE_COMPARISON_RESULT":
                console.log("[MASTER] Face verification details:", {
                  result: message.result,
                  confidence: message.confidence,
                  status: message.status,
                  studentId: message.studentId,
                });
                handleFaceVerificationMessage(message, remoteClientId);
                break;

              case "behavior_alert":
                console.log("[MASTER] Behavior alert details:", {
                  viewerId: message.viewerId,
                  alertType: message.alertType,
                  details: message.details,
                });
                const newAlert = {
                  id: Date.now(),
                  timestamp: new Date(message.timestamp).toLocaleString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                    }
                  ),
                  viewerId: message.viewerId,
                  type: message.alertType
                    .replace("multiple_", "")
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" "),
                  message: message.details.message,
                  values: message.details.values,
                };
                console.log("[MASTER] Created behavior alert:", newAlert);
                state.behaviorAlerts = [
                  newAlert,
                  ...state.behaviorAlerts,
                ].slice(0, 10);
                console.log(
                  "[MASTER] Updated alerts array:",
                  state.behaviorAlerts
                );

                // Only increment unread count if popup is not open
                if (!alertState.openPopups[message.viewerId]) {
                  if (!alertState.readAlertIds[message.viewerId]) {
                    alertState.readAlertIds[message.viewerId] = [];
                  }
                  if (!alertState.unreadAlerts[message.viewerId]) {
                    alertState.unreadAlerts[message.viewerId] = 0;
                  }
                  alertState.unreadAlerts[message.viewerId]++;
                  console.log("[MASTER] Updated unread alerts:", {
                    viewerId: message.viewerId,
                    unreadCount: alertState.unreadAlerts[message.viewerId],
                    readIds: alertState.readAlertIds[message.viewerId],
                  });
                }
                break;

              case "object_alert":
                console.log("[MASTER] Object alert details:", {
                  viewerId: message.viewerId,
                  alertType: message.alertType,
                  details: message.details,
                });

                // Handle prohibited objects alert type
                if (message.alertType === "prohibited_objects") {
                  // Create separate alerts for each type of prohibited object
                  message.details.detectedObjects.forEach((objectType) => {
                    const newObjectAlert = {
                      id: Date.now() + Math.random(), // Ensure unique IDs
                      timestamp: new Date(message.timestamp).toLocaleString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: true,
                        }
                      ),
                      viewerId: message.viewerId,
                      type: "Prohibited Object",
                      objectType: objectType, // Store the specific object type
                      message: `${objectType} detected`, // Customize message for each object
                      detectedObjects: [objectType], // Store only this specific object
                      count: 1, // Each object type starts with count 1
                    };

                    // Check if there's a recent alert (within last 30 seconds) for the same object type
                    const recentAlertIndex = state.behaviorAlerts.findIndex(
                      (alert) =>
                        alert.viewerId === message.viewerId &&
                        alert.objectType === objectType &&
                        Date.now() - new Date(alert.timestamp).getTime() < 30000
                    );

                    if (recentAlertIndex !== -1) {
                      // Update existing alert count
                      state.behaviorAlerts[recentAlertIndex].count++;
                      state.behaviorAlerts[
                        recentAlertIndex
                      ].message = `${objectType} detected (×${state.behaviorAlerts[recentAlertIndex].count})`;
                    } else {
                      // Add new alert
                      state.behaviorAlerts = [
                        newObjectAlert,
                        ...state.behaviorAlerts,
                      ].slice(0, 10);
                    }
                  });
                } else {
                  // Handle other object alerts (like multiple_faces) as before
                  const alertType =
                    message.alertType === "multiple_faces"
                      ? "Multiple Faces"
                      : message.alertType;
                  const alertMessage =
                    message.alertType === "multiple_faces"
                      ? `${message.details.message} (${message.details.faceCount} faces)`
                      : message.details.message;

                  const newObjectAlert = {
                    id: Date.now(),
                    timestamp: new Date(message.timestamp).toLocaleString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                      }
                    ),
                    viewerId: message.viewerId,
                    type: alertType,
                    message: alertMessage,
                    faceCount: message.details.faceCount,
                    count: message.details.count,
                  };

                  state.behaviorAlerts = [
                    newObjectAlert,
                    ...state.behaviorAlerts,
                  ].slice(0, 10);
                }

                // Update unread alerts count
                if (!alertState.openPopups[message.viewerId]) {
                  if (!alertState.readAlertIds[message.viewerId]) {
                    alertState.readAlertIds[message.viewerId] = [];
                  }
                  if (!alertState.unreadAlerts[message.viewerId]) {
                    alertState.unreadAlerts[message.viewerId] = 0;
                  }
                  alertState.unreadAlerts[message.viewerId]++;
                }
                break;

              case "CHAT":
                console.log("[MASTER] Chat message details:", {
                  sender: message.sender,
                  recipient: message.recipient,
                  message: message.message,
                });

                // Handle private messages
                if (message.recipient !== "broadcast") {
                  // Initialize messages array for this sender if it doesn't exist
                  if (!chatState.messages[message.sender]) {
                    chatState.messages[message.sender] = [];
                  }

                  // Add the message to the sender's chat history
                  chatState.messages[message.sender].push({
                    timestamp,
                    message: message.message,
                    sender: `Viewer ${message.sender}`,
                    type: "private",
                  });

                  // Always initialize unread counter if it doesn't exist
                  if (
                    typeof chatState.unreadMessages[message.sender] ===
                    "undefined"
                  ) {
                    chatState.unreadMessages[message.sender] = 0;
                  }

                  // Only increment unread count if we're not currently viewing this chat
                  if (chatState.activeChat !== message.sender) {
                    chatState.unreadMessages[message.sender]++;
                    console.log(
                      "[MASTER] Updated unread messages for",
                      message.sender,
                      ":",
                      chatState.unreadMessages[message.sender]
                    );
                  }
                } else {
                  // Handle broadcast messages
                  if (!chatState.messages.broadcast) {
                    chatState.messages.broadcast = [];
                  }

                  chatState.messages.broadcast.push({
                    timestamp,
                    message: message.message,
                    sender: `Viewer ${message.sender}`,
                    type: "broadcast",
                  });

                  // Initialize broadcast unread counter if needed
                  if (
                    typeof chatState.unreadMessages.broadcast === "undefined"
                  ) {
                    chatState.unreadMessages.broadcast = 0;
                  }

                  // Increment unread count for broadcast if not in broadcast chat
                  if (chatState.activeChat !== "broadcast") {
                    chatState.unreadMessages.broadcast++;
                    console.log(
                      "[MASTER] Updated unread broadcast messages:",
                      chatState.unreadMessages.broadcast
                    );
                  }
                }
                break;

              case "UPLOAD_PHASE_START":
                console.log("[MASTER] Received upload phase start:", message);
                chatState.uploadingStudents[message.studentId] = true;
                break;

              default:
                console.log(
                  "[MASTER] Unhandled message type:",
                  message.type,
                  message
                );
            }
          } catch (e) {
            console.error("[MASTER] Error processing message:", e);
            console.error(
              "[MASTER] Raw message that caused error:",
              event.data
            );
            console.error("[MASTER] Full error details:", {
              name: e.name,
              message: e.message,
              stack: e.stack,
            });
          }
        };

        // Handle data channel state changes
        dataChannel.onopen = () => {
          console.log(`[MASTER] Data channel opened with ${remoteClientId}`);
        };

        dataChannel.onclose = () => {
          console.log(`[MASTER] Data channel closed with ${remoteClientId}`);
        };

        dataChannel.onerror = (error) => {
          console.error(
            `[MASTER] Data channel error with ${remoteClientId}:`,
            error
          );
        };

        // Handle incoming data channels
        peerConnection.ondatachannel = (event) => {
          const incomingChannel = event.channel;
          console.log(`[MASTER] Received data channel from ${remoteClientId}`);

          incomingChannel.onmessage = dataChannel.onmessage;
          incomingChannel.onopen = () =>
            console.log(
              `[MASTER] Incoming data channel opened with ${remoteClientId}`
            );
          incomingChannel.onclose = () =>
            console.log(
              `[MASTER] Incoming data channel closed with ${remoteClientId}`
            );
          incomingChannel.onerror = (error) =>
            console.error(
              `[MASTER] Incoming data channel error with ${remoteClientId}:`,
              error
            );
        };
      }

      // Poll for connection stats
      if (!state.peerConnectionStatsInterval) {
        state.peerConnectionStatsInterval = setInterval(
          () => peerConnection.getStats().then(onStatsReport),
          1000
        );
      }

      // Send any ICE candidates to the other peer
      peerConnection.addEventListener("icecandidate", ({ candidate }) => {
        if (candidate) {
          console.log(
            "[MASTER] Generated ICE candidate for client: " + remoteClientId
          );

          // When trickle ICE is enabled, send the ICE candidates as they are generated.
          if (state.useTrickleICE) {
            console.log(
              "[MASTER] Sending ICE candidate to client: " + remoteClientId
            );
            state.signalingClient.sendIceCandidate(candidate, remoteClientId);
          }
        } else {
          console.log(
            "[MASTER] All ICE candidates have been generated for client: " +
              remoteClientId
          );

          // When trickle ICE is disabled, send the answer now that all the ICE candidates have ben generated.
          if (!state.useTrickleICE) {
            console.log(
              "[MASTER] Sending SDP answer to client: " + remoteClientId
            );
            state.signalingClient.sendSdpAnswer(
              peerConnection.localDescription,
              remoteClientId
            );
          }
        }
      });

      // As remote tracks are received, add them to the remote view
      console.log('Adding peerConnection listener for "track"...');

      peerConnection.addEventListener("track", (event) => {
        console.log(
          "[MASTER] Received remote track from client: " + remoteClientId
        );
        console.log("[MASTER] Current remoteViews before adding:", {
          ...state.remoteViews,
        });

        // Create new video element for this client if it doesn't exist
        if (!state.remoteViews[remoteClientId]) {
          state.remoteViews[remoteClientId] = document.createElement("video");
          state.remoteViews[remoteClientId].autoplay = true;
          state.remoteViews[remoteClientId].playsInline = true;
          state.remoteViews[remoteClientId].controls = true;

          console.log(
            "[MASTER] Created new video element for client:",
            remoteClientId
          );
          console.log("[MASTER] Updated remoteViews:", {
            ...state.remoteViews,
          });
        }

        state.remoteViews[remoteClientId].srcObject = event.streams[0];
        console.log("[MASTER] Set video stream for client:", remoteClientId);
        console.log("[MASTER] Video element properties:", {
          autoplay: state.remoteViews[remoteClientId].autoplay,
          playsInline: state.remoteViews[remoteClientId].playsInline,
          controls: state.remoteViews[remoteClientId].controls,
          srcObject: state.remoteViews[remoteClientId].srcObject,
        });
      });

      // If there's no video/audio, master.localStream will be null. So, we should skip adding the tracks from it.
      if (state.localStream) {
        console.log("There's no audio/video...");
        state.localStream
          .getTracks()
          .forEach((track) =>
            peerConnection.addTrack(track, state.localStream)
          );
      }
      await peerConnection.setRemoteDescription(offer);

      // Create an SDP answer to send back to the client
      console.log("[MASTER] Creating SDP answer for client: " + remoteClientId);
      await peerConnection.setLocalDescription(
        await peerConnection.createAnswer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        })
      );

      // When trickle ICE is enabled, send the answer now and then send ICE candidates as they are generated. Otherwise wait on the ICE candidates.
      if (state.useTrickleICE) {
        console.log("[MASTER] Sending SDP answer to client: " + remoteClientId);
        state.signalingClient.sendSdpAnswer(
          peerConnection.localDescription,
          remoteClientId
        );
      }
      console.log(
        "[MASTER] Generating ICE candidates for client: " + remoteClientId
      );
    });

    state.signalingClient.on(
      "iceCandidate",
      async (candidate, remoteClientId) => {
        console.log(
          "[MASTER] Received ICE candidate from client: " + remoteClientId
        );

        // Add the ICE candidate received from the client to the peer connection
        const peerConnection = state.peerConnectionByClientId[remoteClientId];
        if (peerConnection) {
          try {
            await peerConnection.addIceCandidate(candidate);
          } catch (error) {
            console.warn("[MASTER] Error adding ICE candidate:", error);
          }
        } else {
          console.warn(
            "[MASTER] No peer connection found for:",
            remoteClientId
          );
        }
      }
    );

    state.signalingClient.on("close", async () => {
      console.log("[MASTER] Disconnected from signaling channel");
      state.isMasterConnected = false;
      try {
        const deleteChannelResponse = await state.videoClient
          .deleteSignalingChannel({
            ChannelARN: state.channelARN,
          })
          .promise();
        console.log(
          "[MASTER] --- Delete channel response --- ",
          deleteChannelResponse
        );
      } catch (error) {
        console.warn("[MASTER] Error deleting channel:", error);
        // Continue with cleanup even if channel deletion fails
      }
    });

    state.signalingClient.on("error", () => {
      console.error("[MASTER] Signaling client error");
    });

    console.log("[MASTER] Starting master connection");
    state.signalingClient.open();
  } catch (error) {
    console.error("[MASTER] Error starting master connection:", error);
  }
}

//------------------------------------------------------------------------------
function stopPlayer() {
  state.playerIsStarted = false;
  console.log(`role is '${state.role}'`);
  if (state.role === OPTIONS.ROLE.MASTER) {
    stopPlayerForMaster();
  } else {
    stopPlayerForViewer();
  }
}

//------------------------------------------------------------------------------
async function stopPlayerForMaster() {
  console.log("[MASTER] Stopping master connection");

  // Notify all viewers before closing connections
  await notifyViewersOfMasterDisconnect();

  // Clean up signaling client first
  if (state.signalingClient) {
    state.signalingClient.close();
    state.signalingClient = null;
  }

  // Clean up peer connections
  Object.keys(state.peerConnectionByClientId).forEach((clientId) => {
    if (state.peerConnectionByClientId[clientId]) {
      state.peerConnectionByClientId[clientId].close();
    }
  });
  state.peerConnectionByClientId = {};

  // Clean up local stream
  if (state.localStream) {
    state.localStream.getTracks().forEach((track) => track.stop());
    state.localStream = null;
  }

  // Clear stats interval
  if (state.peerConnectionStatsInterval) {
    clearInterval(state.peerConnectionStatsInterval);
    state.peerConnectionStatsInterval = null;
  }

  // Safely clean up video elements
  if (state.localView && state.localView.current) {
    state.localView.current.srcObject = null;
  }

  // Clean up all remote views safely
  Object.keys(state.remoteViews).forEach((clientId) => {
    if (state.remoteViews[clientId] && state.remoteViews[clientId].srcObject) {
      state.remoteViews[clientId].srcObject = null;
    }
  });
  state.remoteViews = {};

  // Clean up data channels
  if (state.dataChannelByClientId) {
    Object.keys(state.dataChannelByClientId).forEach((clientId) => {
      if (state.dataChannelByClientId[clientId]) {
        state.dataChannelByClientId[clientId].close();
      }
    });
    state.dataChannelByClientId = {};
  }

  // Reset all chat-related state
  state.messages = { broadcast: [] }; // Reset to initial state with empty broadcast array
  state.unreadMessages = {}; // Clear all unread counts
  state.activeChat = "broadcast"; // Reset to broadcast mode
  state.receivedMessages = ""; // Clear legacy messages
  state.behaviorAlerts = []; // Clear alerts
  state.verifiedStudents = {}; // Clear verified students state

  // Clear chatState as well
  chatState.messages = { broadcast: [] };
  chatState.unreadMessages = {};
  chatState.activeChat = "broadcast";

  // Force a re-render by updating playerIsStarted state
  setTimeout(() => {
    state.playerIsStarted = false;
  }, 0);
}

//------------------------------------------------------------------------------
function stopPlayerForViewer() {
  console.log("[VIEWER] Stopping viewer connection");

  // Stop continuous recording
  stopContinuousRecording();

  // Reset all state variables
  state.playerIsStarted = false;
  window.appState = null; // Clear the auto-start flag

  // Clean up signaling client
  if (state.signalingClient) {
    state.signalingClient.close();
    state.signalingClient = null;
  }

  // Clean up peer connection
  if (state.peerConnection) {
    state.peerConnection.close();
    state.peerConnection = null;
  }

  // Clean up local stream
  if (state.localStream) {
    state.localStream.getTracks().forEach((track) => {
      track.stop();
      state.localStream.removeTrack(track);
    });
    state.localStream = null;
  }

  // Clean up remote stream
  if (state.remoteStream) {
    state.remoteStream.getTracks().forEach((track) => {
      track.stop();
      state.remoteStream.removeTrack(track);
    });
    state.remoteStream = null;
  }

  // Clear stats interval
  if (state.peerConnectionStatsInterval) {
    clearInterval(state.peerConnectionStatsInterval);
    state.peerConnectionStatsInterval = null;
  }

  // Clean up video elements
  if (state.localView && state.localView.current) {
    state.localView.current.srcObject = null;
  }

  if (state.remoteView && state.remoteView.current) {
    state.remoteView.current.srcObject = null;
  }

  // Clean up data channel
  if (state.dataChannel) {
    if (state.dataChannel.readyState === "open") {
      state.dataChannel.close();
    }
    state.dataChannel = null;
  }

  // Reset received messages
  state.receivedMessages = "";
}

//------------------------------------------------------------------------------
function onStatsReport(report) {
  // TODO: Publish stats
}

//------------------------------------------------------------------------------
function getRandomClientId() {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${randomStr}`.toUpperCase();
}

// function getRandomExamID() {
//   return Math.random().toString(36).substring(2).toUpperCase();
// }

// function to send disconnect message to all viewers

async function notifyViewersOfMasterDisconnect() {
  console.log("[MASTER] Notifying all viewers of disconnect");

  // Send disconnect message through data channels if they exist
  Object.keys(state.dataChannelByClientId).forEach((clientId) => {
    try {
      if (
        state.dataChannelByClientId[clientId] &&
        state.dataChannelByClientId[clientId].readyState === "open"
      ) {
        state.dataChannelByClientId[clientId].send(
          JSON.stringify({
            type: "MASTER_DISCONNECT",
            message: "Master is disconnecting",
          })
        );
      }
    } catch (e) {
      console.warn(
        `[MASTER] Failed to send disconnect message to client ${clientId}:`,
        e
      );
    }
  });

  await new Promise((resolve) => setTimeout(resolve, 500));
}

// Add a function to check master's status
async function checkMasterStatus() {
  try {
    const kinesisVideoClient = new KinesisVideo({
      region: config.aws.region,
      endpoint: state.endpoint || null,
      correctClockSkew: true,
      accessKeyId: config.aws.credentials.accessKeyId,
      secretAccessKey: config.aws.credentials.secretAccessKey,
      sessionToken: null,
    });

    // Simply try to describe the channel
    await kinesisVideoClient
      .describeSignalingChannel({
        ChannelName: state.channelName,
      })
      .promise();

    // If we get here, channel exists which means master is streaming
    state.isMasterConnected = true;
    return true;
  } catch (error) {
    if (error.code === "ResourceNotFoundException") {
      // Channel doesn't exist = master hasn't started streaming
      console.log("[VIEWER] Waiting for master to start streaming...");
    } else {
      console.error("[VIEWER] Error checking master status:", error);
    }
    state.isMasterConnected = false;
    return false;
  }
}

// section start-------------------video recording and storing-----------------------------

// function to wait for specified duration
const wait = (timeInMs) =>
  new Promise((resolve) => setTimeout(resolve, timeInMs));

// function to check storage quota
async function checkStorageQuota() {
  if (navigator.storage && navigator.storage.estimate) {
    const { usage, quota } = await navigator.storage.estimate();
    const percentageUsed = (usage / quota) * 100;
    console.log(`Storage usage: ${percentageUsed.toFixed(2)}% of quota`);
    return percentageUsed < 90;
  }
  return true; // If can't check quota, assume it's ok
}

// function to start recording
async function startRecording(stream, lengthInMS = 10000) {
  try {
    // Check storage quota before starting
    const hasSpace = await checkStorageQuota();
    if (!hasSpace) {
      console.error("Not enough storage space for recording");
      return null;
    }

    const options = {
      mimeType: "video/webm;codecs=vp8,opus",
      videoBitsPerSecond: 1000000,
    };

    const recorder = new MediaRecorder(stream, options);
    const data = [];

    recorder.ondataavailable = (event) => data.push(event.data);
    recorder.start(5000); // Collect data in 5-second chunks
    console.log(`Recording started for ${lengthInMS / 1000} seconds...`);

    const stopped = new Promise((resolve, reject) => {
      recorder.onstop = resolve;
      recorder.onerror = (event) => reject(event.name);
    });

    const recorded = wait(lengthInMS).then(() => {
      if (recorder.state === "recording") {
        recorder.stop();
      }
    });

    await Promise.all([stopped, recorded]);
    return new Blob(data, { type: "video/webm" });
  } catch (error) {
    console.error("Error during recording:", error);
    return null;
  }
}

// function to trigger download
// function downloadRecording(blob, timestamp) {
//   const url = window.URL.createObjectURL(blob);
//   const a = document.createElement("a");
//   a.style.display = "none";
//   a.href = url;
//   a.download = `recording-${timestamp}.webm`;
//   document.body.appendChild(a);
//   a.click();
//   window.URL.revokeObjectURL(url);
//   document.body.removeChild(a);
// }

//function to handle S3 uploads
async function uploadToS3(blob, timestamp) {
  try {
    console.log(
      "[VIEWER] Starting S3 upload simulation for recording:",
      timestamp
    );

    // Simulate upload delay
    await new Promise((resolve) => setTimeout(resolve, 1000));
    console.log("[VIEWER] Upload successful (simulated)");

    /* Commented S3 upload code*/
    const s3 = new AWS.S3({
      region: config.aws.region,
      accessKeyId: config.aws.credentials.accessKeyId,
      secretAccessKey: config.aws.credentials.secretAccessKey,
    });

    const chunkNumber = String(Math.floor(Date.now() / 1000)).slice(-2);
    const key = `${state.channelName}/${state.clientId}/${state.clientId}_chunk${chunkNumber}_${timestamp}.webm`;

    const params = {
      Bucket: "proctoring-system",
      Key: key,
      Body: blob,
      ContentType: "video/webm",
      Metadata: {
        "exam-id": state.channelName,
        "student-id": state.clientId,
        timestamp: timestamp,
        "chunk-number": chunkNumber,
        size: blob.size.toString(),
      },
    };

    console.log("[VIEWER] Uploading to S3...", key);
    await s3.putObject(params).promise();
    console.log("[VIEWER] Upload successful for chunk:", chunkNumber);

    return true;
  } catch (error) {
    console.error("[VIEWER] Error in upload simulation:", error);
    return false;
  }
}

// Simplified continuous recording function
async function startContinuousRecording(stream) {
  console.log("[VIEWER] Starting continuous recording process");
  if (state.isRecording) {
    console.log("[VIEWER] Recording already in progress, skipping");
    return;
  }
  state.isRecording = true;

  async function recordSegment() {
    try {
      console.log("[VIEWER] Beginning new recording segment");
      const recordedBlob = await startRecording(stream, 10000); // 10 seconds

      if (recordedBlob && state.isRecording) {
        const size = (recordedBlob.size / (1024 * 1024)).toFixed(2);
        console.log(`[VIEWER] Segment recording completed. Size: ${size}MB`);

        // Simulate upload
        const timestamp = new Date().toISOString();
        const uploaded = await uploadToS3(recordedBlob, timestamp);

        if (uploaded && state.isRecording) {
          console.log("[VIEWER] Starting next segment...");
          recordSegment(); // Start next recording
        } else {
          console.log("[VIEWER] Upload simulation failed or recording stopped");
        }
      }
    } catch (error) {
      console.error("[VIEWER] Error in recording segment:", error);
      if (state.isRecording) {
        console.log("[VIEWER] Retrying recording in 1 second...");
        setTimeout(recordSegment, 1000);
      }
    }
  }

  console.log("[VIEWER] Initiating first recording segment");
  recordSegment();
}

// function to stop continuous recording
function stopContinuousRecording() {
  console.log("[VIEWER] Stopping continuous recording");
  state.isRecording = false;
}

// section end -----video recording and storing------------------------

// S3_bucket/u001/exam_videos/dsa0_'date'_'randomid'/studentId/studentId_chunk1_timestamp
export default KinesisWebRTC;

// Add the handler function at the top level (after state definitions)
const handleFaceVerificationMessage = (message, viewerId) => {
  if (message.type === "FACE_COMPARISON_RESULT") {
    state.faceVerificationStatus[viewerId] = {
      status: message.result,
      confidence: message.confidence || 0,
    };
  }
};

// Add this function near other connection management functions
const cleanupConnectionForClient = (clientId) => {
  if (state.peerConnectionByClientId[clientId]) {
    state.peerConnectionByClientId[clientId].close();
    delete state.peerConnectionByClientId[clientId];
  }

  if (state.dataChannelByClientId[clientId]) {
    state.dataChannelByClientId[clientId].close();
    delete state.dataChannelByClientId[clientId];
  }
};

// Replace the existing cleanup code with this safer version
const cleanupConnections = () => {
  Object.keys(state.peerConnectionByClientId).forEach((clientId) => {
    if (!state.verifiedStudents[clientId]) {
      // Only cleanup unverified students
      cleanupConnectionForClient(clientId);
    }
  });
};
